import { graphql } from 'react-relay';
import React from 'react';
import { styled } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import LinkButton from '@atlaskit/button/link';
import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';

import { VIEW_CONTENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceAbort,
	ExperienceFailure,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { BlankExperience } from '@confluence/blank-experience';
import { SPACE_SETTINGS_DETAILS } from '@confluence/named-routes/entry-points/routes.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	OverviewNotFoundSpaceQuery as OverviewNotFoundSpaceQuery$data,
	OverviewNotFoundSpaceQueryVariables as OverviewNotFoundSpaceQuery$variables,
} from './__types__/OverviewNotFoundSpaceQuery.ts';
import { NOT_FOUND_PAGE_LOAD } from './perf.config.ts';
type Props = {
	spaceKey: string;
};

const imageStyles = {
	width: 151,
	height: 153,
	backgroundImage: `url(${require('./icon-space-overview-empty.svg')})`,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderContainer = styled.div({
	display: 'flex',
	padding: `${token('space.300')} ${token('space.500')} ${token('space.400')}`,
	marginBottom: token('space.negative.400'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& h1': {
		fontWeight: token('font.weight.regular'),
	},
});

export const OverviewNotFound: React.FC<Props> = ({ spaceKey }) => {
	const { data, loading, error } = useQueryInterop<
		OverviewNotFoundSpaceQuery$data,
		OverviewNotFoundSpaceQuery$variables
	>(
		graphql`
			query OverviewNotFoundSpaceQuery($spaceKey: String) {
				space(key: $spaceKey) {
					id
					name
				}
			}
		`,
		{
			variables: {
				spaceKey,
			},
		},
	);

	if (loading) {
		return null;
	}

	if (error) {
		if (isExpectedApolloError(error)) {
			return <ExperienceAbort name={VIEW_CONTENT_EXPERIENCE} reason="Expected apollo error" />;
		}
		return <ExperienceFailure name={VIEW_CONTENT_EXPERIENCE} error={error} />;
	}

	// preventing flicker
	if (!data || !data.space) {
		return <ExperienceAbort name={VIEW_CONTENT_EXPERIENCE} reason="Space not found" />;
	}

	return (
		<div>
			<HeaderContainer>
				<Heading as="h1" size="xlarge" id="title-text" testId="title-text">
					{data.space.name}
				</Heading>
			</HeaderContainer>
			<div data-test-id="space-overview-blank-experience">
				<BlankExperience
					imageStyles={imageStyles}
					mainBanner={
						<FormattedMessage
							id="not-found.overview.blankExperience.mainBanner"
							defaultMessage="Well, this is a bit awkward."
							description="funny message to let user know we couldn't find their page"
						/>
					}
					subBanner={
						<FormattedMessage
							id="not-found.overview.blankExperience.subBanner"
							defaultMessage="This space doesn’t have a homepage so there’s nothing to display here. Sad panda."
							description="funny message to let user know we couldn't find their page"
						/>
					}
					subBanner2={
						<FormattedMessage
							id="not-found.overview.blankExperience.subBanner2"
							defaultMessage="Add a homepage now to spruce things up, and use it to tell your visitors what this space is all about!"
							description="funny message to let user know we couldn't find their page"
						/>
					}
					callToAction={
						<LinkButton appearance="primary" href={SPACE_SETTINGS_DETAILS.toUrl({ spaceKey })}>
							<FormattedMessage
								id="not-found.set.space.home"
								defaultMessage="Set space home"
								description="Button to create a new space homepage"
							/>
						</LinkButton>
					}
				/>
				<PageLoadEnd metric={NOT_FOUND_PAGE_LOAD} customData={{ isBlankExperience: true }} />
				<ExperienceSuccess name={VIEW_CONTENT_EXPERIENCE} />
			</div>
		</div>
	);
};

OverviewNotFound.displayName = 'SpaceOverviewBlankExperience';
